import PropTypes from 'prop-types';
import React from 'react';

import { AnalyticsService, REDIRECTIONS, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';
import { Vehicle } from '@sm360/widget-commons-vehicle';
import Logger from '@sm360/logger-sentry';
import {
    LocaleService,
    WindowProxy,
    UrlService,
    ConfigsService as GeneralConfigService,
} from '@sm360/widget-commons-core-services';

import 'vdp-used/components/PhotosAndBodyAndCertifiedSection/Carfax/Carfax.scss';

class Carfax extends React.Component {
    constructor(props) {
        super(props);

        this.mutationObserver = null;

        this.handleMutations = this.handleMutations.bind(this);
        this.openLeadGeneratorWindow = this.openLeadGeneratorWindow.bind(this);
    }

    componentDidMount() {
        const $window = WindowProxy.get();
        if (!this.props.carfaxKey || !$window) {
            return;
        }

        /*
         * If the lead generator is enabled (carfaxLeadGenerator), then we want to detect when Carfax's script has
         * added their content in the designated <div>, and hijack the <a> so that it opens the lead generator window.
         *
         * This feature is NOT covered by unit or end-to-end tests. Explanation:
         * Unit test: We'd have to wait for the external script to inject the content, which would be unreliable, and we
         * can't mock it by injecting content ourselves since Enzyme wrappers are immutable.
         * End-to-end test: When running in Cypress' iFrame, the external script does not inject the content.
         */
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }

        try {
            this.mutationObserver = new MutationObserver(this.handleMutations);
            this.mutationObserver.observe($window.document.getElementById('carproof-badge'), { childList: true });
        } catch (e) {
            const logger = new Logger(this.constructor.displayName, GeneralConfigService.getLoggerContext());
            logger.error(e.message);
        }

        /*
            We are now using a third party script made by carfax that allows us
            to display a form to generate a lead before displaying the report.
            This part downloads the script.
        */
        // if (!$window.document.getElementById('carproof-leadgenerator-js')) {
        //     const script = $window.document.createElement('script');
        //     script.defer = true;
        //     script.src = 'https://webservice.carproof.com/LeadGenerator/js/leadgen.js';
        //     script.id = 'carproof-leadgenerator-js';
        //     $window.document.head.appendChild(script);
        // }
    }

    handleMutations(mutations) {
        if (mutations.length < 1) {
            return;
        }

        const addedDiv = mutations[0].addedNodes.item(0);
        if (!addedDiv) {
            // If the carproof script didn't add the badge we need to reAdd the link.
            const parent = mutations[0].target;
            const child = mutations[0].removedNodes[0];
            parent.appendChild(child);
        } else if (this.props.carfaxLeadGenerator) {
            // here we'll open the lead generator by adding a click even listener
            const a = addedDiv.getElementsByTagName('a').item(0);
            if (a) {
                a.removeAttribute('target');
                a.setAttribute('href', '#');
                a.addEventListener('click', (event) => this.openLeadGeneratorWindow(event));
                a.id = 'carfax-lead-generator-button';
            }
        }
    }

    openLeadGeneratorWindow(event) {
        event.preventDefault();

        const { carfaxKey, vehicle } = this.props;
        if (!carfaxKey || !vehicle) {
            return;
        }

        /*
            The following is a call to the constructor function provided by the new
            carfax third party script which will open a window with the form.
        */
        // eslint-disable-next-line no-undef
        const cp = new CarProof(this.props.carfaxKey);
        cp.openLeadGenADF(
            vehicle.vin,
            '',
            LocaleService.getSimpleLocale(),
            vehicle.year,
            vehicle.getMake(),
            vehicle.getModel()
        );
    }

    render() {
        const { vehicle, showCarfax } = this.props;
        const { BadgesImageUrl: carfaxBadgeUrl, VhrReportUrl: carfaxReportUrl } =
            (vehicle?.carfax?.BadgesList && vehicle?.carfax?.BadgesList.length > 0 && vehicle?.carfax?.BadgesList[0]) ||
            {};

        let carfaxUrl = '';
        const locale = LocaleService.getSimpleLocale();
        carfaxUrl = REDIRECTIONS[locale].carfaxForm;
        carfaxUrl = UrlService.replaceParamWithValue(carfaxUrl, '{make}', vehicle?.getMake());
        carfaxUrl = UrlService.replaceParamWithValue(carfaxUrl, '{model}', vehicle?.getModel());
        carfaxUrl = UrlService.replaceParamWithValue(carfaxUrl, '{year}', vehicle?.getYear());
        carfaxUrl = UrlService.replaceParamWithValue(carfaxUrl, '{trim}', vehicle?.getModelTrimCode());
        carfaxUrl = UrlService.replaceParamWithValue(carfaxUrl, '{stock}', vehicle?.getStockNumber());
        carfaxUrl = UrlService.replaceParamWithValue(carfaxUrl, '{id}', vehicle?.getVehicleId());
        carfaxUrl = carfaxUrl.replace(/\s/g, '-');

        const trackLinkClick = () => AnalyticsService.gaTrigger(TAGS.CARFAX_REPORT(WindowProxy.location().pathname));

        return (
            showCarfax && (
                <div className="showroom-vdp-used-carfax">
                    <div className="carfax-logo">
                        <img
                            className="img-carfax"
                            alt={LocaleService.t('VDP_USED.CARFAX.BADGE_TITLE')}
                            src="//s3.amazonaws.com/fs-sm360/var/images/sm360/icone/carproof_badge.png"
                        />
                        <p className="is-light">{LocaleService.t('VDP_USED.CARFAX.BADGE_TITLE')}</p>
                    </div>
                    <div className="carfax-list carfax-content">
                        <div className="carfax-title">{LocaleService.t('VDP_USED.CARFAX.TITLE')}</div>
                        <div className="carfax-items-list">
                            {!carfaxBadgeUrl &&
                                !carfaxReportUrl && (
                                    <a
                                        href={carfaxUrl}
                                        title={LocaleService.t('VDP_USED.CARFAX.REPORT')}
                                        className="sr-link"
                                        onClick={trackLinkClick}
                                    >
                                        {LocaleService.t('VDP_USED.CARFAX.REPORT')}
                                    </a>
                                )}

                            {carfaxBadgeUrl &&
                                carfaxReportUrl && (
                                    <a
                                        href={carfaxReportUrl}
                                        title={LocaleService.t('VDP_USED.CARFAX.REPORT')}
                                        className="sr-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            className="carfax-badge-img"
                                            src={carfaxBadgeUrl}
                                            alt={LocaleService.t('CARFAX.TITLE')}
                                        />
                                    </a>
                                )}
                        </div>
                    </div>
                </div>
            )
        );
    }
}

Carfax.defaultProps = {
    carfaxLeadGenerator: false,
};

Carfax.propTypes = {
    carfaxKey: PropTypes.string.isRequired,
    carfaxLeadGenerator: PropTypes.bool,
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModelV25).isRequired,
    showCarfax: PropTypes.bool.isRequired,
};

Carfax.displayName = 'Carfax';

export default Carfax;
